import { useMedplum } from '@medplum/react-hooks';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { resolveIntakeOrganizationIdFromPatient } from '@/lib/resolveIntakeOrganizationFromPatient';
import { usePortalSubject } from '../../contexts/PortalSubjectContext';
import { ClientIntakePage } from '../client-intake';
import type { TherapyType } from '../public/intake/types';
import { ClientPortalLayout } from './components/ClientPortalLayout';
import { fetchActiveEpisodeTherapyTypeCodes } from './utils/portalEoc';

export function ClientPortalRequestServicePage(): JSX.Element {
  const { activePatientId } = usePortalSubject();
  const medplum = useMedplum();
  const navigate = useNavigate();
  const [excludedTypeCodes, setExcludedTypeCodes] = useState<Set<string>>(() => new Set());
  const [intakeOrganizationId, setIntakeOrganizationId] = useState<string | undefined>();

  useEffect(() => {
    if (!activePatientId) {
      setExcludedTypeCodes(new Set());
      setIntakeOrganizationId(undefined);
      return;
    }
    let cancelled = false;
    void fetchActiveEpisodeTherapyTypeCodes(medplum, activePatientId).then((codes) => {
      if (!cancelled) setExcludedTypeCodes(codes);
    });
    void resolveIntakeOrganizationIdFromPatient(medplum, activePatientId)
      .then((orgId) => {
        if (!cancelled) setIntakeOrganizationId(orgId);
      })
      .catch(() => {
        if (!cancelled) setIntakeOrganizationId(undefined);
      });
    return () => {
      cancelled = true;
    };
  }, [activePatientId, medplum]);

  const excludedTherapyTypes = useMemo(
    () => [...excludedTypeCodes] as TherapyType[],
    [excludedTypeCodes],
  );

  return (
    <ClientPortalLayout
      title="Request a service"
      description="Tell us what you're looking for and we'll match you with a provider."
      fullBleed
    >
      <ClientIntakePage
        billingPatientId={activePatientId}
        flowVariant="request-service"
        excludedTherapyTypes={excludedTherapyTypes}
        intakeOrganizationId={intakeOrganizationId}
        onComplete={() => navigate('/client-portal')}
        completionCloseLabel="Returning to your portal…"
      />
    </ClientPortalLayout>
  );
}
